import * as Yup from "yup";
import { types } from "./types";
import { validatorTypes } from "./validatorTypes";
import { formatUnderscoreToSpace } from "./text-format";

// "deal_category_id" -> "Deal Category Id", used in every validation message
// so the admin always knows which field failed.
export const fieldLabel = (name = "") =>
  formatUnderscoreToSpace(name).replace(/\b\w/g, (c) => c.toUpperCase());

// ui.validation overrides the backend validation when it is set.
export const columnValidations = (column) =>
  column?.ui?.validation?.length ? column.ui.validation : column?.validation || [];

export const isRequiredColumn = (column) =>
  columnValidations(column).includes(validatorTypes.required);

// Empty inputs (cleared number field, cleared dropdown) come through as "".
const emptyToNull = (value, original) => (original === "" ? null : value);

const schemaFor = (validations, label) => {
  let schema;

  if (validations.includes(validatorTypes.string)) {
    schema = Yup.string().typeError(`${label} must be text`);
  } else if (validations.includes(validatorTypes.email)) {
    schema = Yup.string().email(`${label} must be a valid email`);
  } else if (validations.includes(validatorTypes.uuid)) {
    schema = Yup.string().uuid(`${label} has an invalid value`);
  } else if (validations.includes(validatorTypes.isInteger)) {
    schema = Yup.number()
      .typeError(`${label} must be a number`)
      .integer(`${label} must be a whole number`);
  } else if (validations.includes(validatorTypes.number)) {
    schema = Yup.number().typeError(`${label} must be a number`);
  } else if (validations.includes(validatorTypes.bool)) {
    schema = Yup.boolean();
  } else if (validations.includes(validatorTypes.date)) {
    schema = Yup.date().typeError(`${label} must be a valid date`);
  } else if (validations.includes(validatorTypes.array)) {
    schema = Yup.array().typeError(`${label} must be a list`);
  } else if (validations.includes(validatorTypes.url)) {
    schema = Yup.string().url(`${label} must be a valid URL`);
  } else {
    schema = Yup.mixed();
  }

  schema = schema.transform(emptyToNull).nullable();

  if (validations.includes(validatorTypes.required)) {
    schema = schema.required(`${label} is required`);
    if (schema.type === "string") {
      // Only spaces counts as empty too
      schema = schema.test("not-blank", `${label} is required`, (v) => !!v?.trim());
    }
    if (schema.type === "array") {
      schema = schema.min(1, `${label} needs at least one item`);
    }
  } else {
    schema = schema.notRequired();
  }

  return schema;
};

// mode is "create" or "edit": only fields shown on that form are validated,
// otherwise a hidden required field would silently block the submit.
export const buildValidationSchema = (columns, mode) =>
  Yup.object(
    columns.reduce((shape, column) => {
      if (column?.ui?.[mode] === false || column?.ui?.display === false) {
        return shape;
      }
      shape[column.name] = schemaFor(
        columnValidations(column),
        fieldLabel(column.name),
      );
      return shape;
    }, {}),
  );

// Non-text columns (integer, uuid, date, json) can't store "" in the
// database, so send null when the admin leaves them empty.
export const cleanEmptyValues = (values, columns) =>
  Object.keys(values).reduce((data, key) => {
    const column = columns.find((c) => c.name === key);
    const isText =
      column?.type === types.STRING || column?.type === types.TEXT;
    data[key] = values[key] === "" && column && !isText ? null : values[key];
    return data;
  }, {});
